import { useMemo } from "react";
import { ChevronRight, Clock, Locate } from "lucide-react";
import { catColor, CategoryIcon } from "@/lib/categories";

function ItemButton({ it, onSelect, onHover }) {
  return (
    <button
      data-testid={`list-item-${it.permid}`}
      onClick={() => onSelect(it)}
      onMouseEnter={() => onHover && onHover(it.permid)}
      onMouseLeave={() => onHover && onHover(null)}
      className="flex w-full items-center gap-3 rounded-2xl p-2.5 text-left transition-colors hover:bg-slate-50"
    >
      <span
        className="grid h-10 w-10 shrink-0 place-items-center rounded-xl shadow-sm"
        style={{ background: catColor(it.category) }}
      >
        <CategoryIcon category={it.category} color="#fff" size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-slate-900">
          {it.title || "Ohne Titel"}
        </p>
        <p className="truncate text-sm text-slate-500">
          {it.category || "Ohne Kategorie"}
          {it.address ? ` · ${it.address}` : ""}
        </p>
      </div>
      <ChevronRight size={18} className="shrink-0 text-slate-300" />
    </button>
  );
}

export default function InfrastructureList({
  items,
  loading,
  totalItems,
  authed,
  onSelect,
  onHover,
  onRecenter,
  mode = "category",
}) {
  const groups = useMemo(() => {
    const map = {};
    (items || []).forEach((it) => {
      const c = it.category || "Ohne Kategorie";
      (map[c] = map[c] || []).push(it);
    });
    return Object.keys(map)
      .sort((a, b) => a.localeCompare(b, "de"))
      .map((cat) => ({
        cat,
        items: map[cat].sort((a, b) =>
          (a.title || "").localeCompare(b.title || "", "de")
        ),
      }));
  }, [items]);

  const recent = useMemo(() => {
    return [...(items || [])].sort(
      (a, b) => (Number(b.lastUpdate) || 0) - (Number(a.lastUpdate) || 0)
    );
  }, [items]);

  if (loading) {
    return (
      <div className="px-3 py-10 text-center text-sm text-slate-400">Lädt…</div>
    );
  }

  if (totalItems === 0) {
    return (
      <div className="px-4 py-12 text-center">
        <p className="font-display text-lg font-semibold text-slate-700">
          Noch keine Objekte
        </p>
        <p className="mt-1 text-sm text-slate-400">
          {authed
            ? "Tippe auf +, um dein erstes Objekt anzulegen."
            : "Melde dich an, um Objekte anzulegen."}
        </p>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="px-4 py-12 text-center">
        <p className="text-sm text-slate-400">
          Keine Einträge im sichtbaren Kartenausschnitt.
        </p>
        {onRecenter && (
          <button
            onClick={onRecenter}
            data-testid="recenter-button"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-amber-500/30 transition-transform hover:scale-[1.03] active:scale-[0.97]"
          >
            <Locate size={16} /> Alle Objekte anzeigen
          </button>
        )}
      </div>
    );
  }

  if (mode === "recent") {
    return (
      <div data-testid="item-list" className="px-2 pb-4 pt-1">
        <div className="mb-1 flex items-center gap-2 px-3 py-2">
          <Clock size={12} className="text-primary" />
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
            Neueste zuerst
          </span>
        </div>
        {recent.map((it) => (
          <ItemButton key={it.permid} it={it} onSelect={onSelect} onHover={onHover} />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="item-list" className="pb-4">
      {groups.map((g) => (
        <div key={g.cat} className="mb-1">
          <div
            className="sticky top-0 z-10 flex items-center gap-2 bg-white/95 px-4 py-2 backdrop-blur"
            data-testid={`category-header-${g.cat}`}
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: catColor(g.cat) }}
            />
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
              {g.cat}
            </span>
            <span className="text-xs font-semibold text-slate-300">
              {g.items.length}
            </span>
          </div>
          <div className="px-2">
            {g.items.map((it) => (
              <ItemButton key={it.permid} it={it} onSelect={onSelect} onHover={onHover} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
